import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, Heart, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TestimonialModal = ({ isOpen, onClose, onSubmit, onSkip, serviceType = '' }) => {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Por favor, escribe tu opinión');
      return;
    }

    setSubmitting(true);
    try {
      const clientToken = sessionStorage.getItem('client_token');
      const response = await fetch(`${API}/client/testimonial`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(clientToken ? { 'Authorization': `Bearer ${clientToken}` } : {})
        },
        body: JSON.stringify({
          content: content.trim(),
          rating,
          service_type: serviceType
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al enviar');
      }

      toast.success('¡Gracias por tu testimonio!');
      onSubmit?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-background border border-border w-full max-w-lg overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-border text-center relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-500/10 flex items-center justify-center">
              <Heart className="w-8 h-8 text-rose-500" />
            </div>
            <h2 className="font-serif text-2xl mb-2">¿Te ha gustado mi trabajo?</h2>
            <p className="text-muted-foreground text-sm">
              Tu opinión me ayuda a seguir mejorando y a que otros clientes me conozcan
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Rating */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-3">¿Cómo valorarías tu experiencia?</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'text-amber-500 fill-amber-500'
                          : 'text-muted-foreground'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {rating === 5 && '¡Excelente!'}
                {rating === 4 && 'Muy bien'}
                {rating === 3 && 'Bien'}
                {rating === 2 && 'Regular'}
                {rating === 1 && 'Mejorable'}
              </p>
            </div>

            {/* Comment */}
            <div>
              <label className="text-sm text-muted-foreground block mb-2">
                Cuéntanos tu experiencia
              </label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="¿Qué te ha parecido la sesión? ¿Qué destacarías de las fotos?"
                rows={4}
                className="resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-border flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => {
                onSkip?.();
                onClose();
              }}
              className="flex-1"
            >
              Omitir y descargar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !content.trim()}
              className="flex-1 gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-background border-t-transparent rounded-full animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar y descargar
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TestimonialModal;
